import React, { useState, useEffect, useMemo } from 'react';
import { useI18n } from '../../context/I18nContext';
import { authService } from '../../services/authService';
// Note: Database uses risk_name, not title; and snake_case fields
interface DBRisk {
  id: number;
  framework_id?: number;
  risk_name: string;
  description?: string;
  risk_level?: string;
  likelihood: number;
  impact: number;
  owner?: string;
  status: string;
  treatment?: string;
  residual_likelihood?: number;
  residual_impact?: number;
  next_review_date?: string;
}
import { grcService } from '../../services/grcService';
import { 
  Plus, Edit, Trash2, Shield, AlertTriangle, AlertOctagon,
  Download, RefreshCw, Search, Filter,
  Layers, Server, Database, Users, FileText, Building, Cpu,
  TrendingUp, TrendingDown, Minus
} from 'lucide-react';

interface UnifiedRiskRegisterProps {
  userRole: 'admin' | 'editor' | 'viewer';
  onRiskSelect?: (risk: DBRisk) => void;
  initialFilters?: { id?: number; level?: string; status?: string };
}

export const UnifiedRiskRegister: React.FC<UnifiedRiskRegisterProps> = ({
  userRole,
  onRiskSelect,
  initialFilters
}) => {
  const { t } = useI18n();
  const [risks, setRisks] = useState<DBRisk[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRisk, setEditingRisk] = useState<DBRisk | null>(null);
  const [selectedRiskId, setSelectedRiskId] = useState<number | null>(null);
  const selectedRiskRef = React.useRef<HTMLDivElement>(null);

  // Check permissions (section-based RBAC)
  const canEdit = authService.hasPermission('risks', 'edit') || authService.isGlobalAdmin() || authService.isOrgAdmin();

  // Apply initial filters from props
  useEffect(() => {
    if (initialFilters?.id) {
      setSelectedRiskId(initialFilters.id);
    }
    if (initialFilters?.level) {
      setLevelFilter(initialFilters.level);
    }
    if (initialFilters?.status) {
      setStatusFilter(initialFilters.status);
    }
  }, [initialFilters]);

  useEffect(() => {
    loadRisks();
  }, []);

  // Scroll to selected risk when it becomes available
  useEffect(() => {
    if (selectedRiskId && !loading && risks.length > 0) {
      setTimeout(() => {
        selectedRiskRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [selectedRiskId, loading, risks]);

  const loadRisks = async () => {
    setLoading(true);
    const data = await grcService.getGlobalRisks() as unknown as DBRisk[];
    setRisks(data);
    setLoading(false);
  };

  // Compute risk level from likelihood/impact if not stored
  const getRiskLevel = (r: DBRisk): string => {
    if (r.risk_level) return r.risk_level;
    const score = (r.likelihood || 1) * (r.impact || 1);
    if (score >= 20) return 'Critical';
    if (score >= 12) return 'High';
    if (score >= 6) return 'Medium';
    return 'Low';
  };

  const filteredRisks = useMemo(() => {
    let result = risks;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(r =>
        r.risk_name?.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query) ||
        r.owner?.toLowerCase().includes(query)
      );
    }

    if (levelFilter !== 'all') {
      result = result.filter(r => getRiskLevel(r) === levelFilter);
    }

    if (statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter);
    }

    return result;
  }, [risks, searchQuery, levelFilter, statusFilter]);

  // Group by risk level for summary
  const stats = useMemo(() => ({
    total: risks.length,
    critical: risks.filter(r => getRiskLevel(r) === 'Critical').length,
    high: risks.filter(r => getRiskLevel(r) === 'High').length,
    medium: risks.filter(r => getRiskLevel(r) === 'Medium').length,
    low: risks.filter(r => getRiskLevel(r) === 'Low').length,
    open: risks.filter(r => r.status === 'open').length,
    mitigated: risks.filter(r => r.status === 'mitigated').length
  }), [risks]);

  const handleDelete = async (id: number) => {
    if (!confirm(t.common.confirmDelete || 'Are you sure you want to delete this risk?')) return;
    try {
      const success = await grcService.deleteRisk(id);
      if (success) {
        await loadRisks();
      }
    } catch (e) {
      console.error('Failed to delete risk:', e);
    }
  };

  const calcRiskLevel = (likelihood: number, impact: number): string => {
    const score = likelihood * impact;
    if (score >= 20) return 'Critical';
    if (score >= 12) return 'High';
    if (score >= 6) return 'Medium';
    return 'Low';
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Critical': return 'bg-red-500';
      case 'High': return 'bg-orange-500';
      case 'Medium': return 'bg-amber-500';
      case 'Low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getLevelBadgeClass = (level: string) => {
    switch (level) {
      case 'Critical': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800';
      case 'High': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800';
      case 'Medium': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800';
      case 'Low': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <AlertOctagon className="w-4 h-4 text-red-500" />;
      case 'mitigated': return <Shield className="w-4 h-4 text-green-500" />;
      case 'accepted': return <Minus className="w-4 h-4 text-blue-500" />;
      case 'closed': return <Shield className="w-4 h-4 text-gray-400" />;
      default: return <AlertTriangle className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
            <AlertOctagon className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t.risks.register}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t.risks.summary}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadRisks}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          {canEdit && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t.actions.add} {t.nav.risks}
            </button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          <div className="text-xs text-gray-500">{t.common.total} {t.nav.risks}</div>
        </div>
        <div 
          onClick={() => setLevelFilter(levelFilter === 'Critical' ? 'all' : 'Critical')}
          className={`bg-white dark:bg-gray-800 rounded-lg p-4 border cursor-pointer transition-colors ${
            levelFilter === 'Critical' ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-200 dark:border-gray-700 hover:border-red-300'
          }`}
        >
          <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
          <div className="text-xs text-gray-500">{t.status.critical}</div>
        </div>
        <div 
          onClick={() => setLevelFilter(levelFilter === 'High' ? 'all' : 'High')}
          className={`bg-white dark:bg-gray-800 rounded-lg p-4 border cursor-pointer transition-colors ${
            levelFilter === 'High' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
          }`}
        >
          <div className="text-2xl font-bold text-orange-600">{stats.high}</div>
          <div className="text-xs text-gray-500">{t.risks.high}</div>
        </div>
        <div 
          onClick={() => setLevelFilter(levelFilter === 'Medium' ? 'all' : 'Medium')}
          className={`bg-white dark:bg-gray-800 rounded-lg p-4 border cursor-pointer transition-colors ${
            levelFilter === 'Medium' ? 'border-amber-500 ring-2 ring-amber-200' : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'
          }`}
        >
          <div className="text-2xl font-bold text-amber-600">{stats.medium}</div>
          <div className="text-xs text-gray-500">{t.risks.moderate}</div>
        </div>
        <div 
          onClick={() => setLevelFilter(levelFilter === 'Low' ? 'all' : 'Low')}
          className={`bg-white dark:bg-gray-800 rounded-lg p-4 border cursor-pointer transition-colors ${
            levelFilter === 'Low' ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200 dark:border-gray-700 hover:border-green-300'
          }`}
        >
          <div className="text-2xl font-bold text-green-600">{stats.low}</div>
          <div className="text-xs text-gray-500">{t.risks.low}</div>
        </div>
        <div 
          onClick={() => setStatusFilter(statusFilter === 'open' ? 'all' : 'open')}
          className={`bg-white dark:bg-gray-800 rounded-lg p-4 border cursor-pointer transition-colors ${
            statusFilter === 'open' ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-200 dark:border-gray-700 hover:border-red-300'
          }`}
        >
          <div className="text-2xl font-bold text-red-600">{stats.open}</div>
          <div className="text-xs text-gray-500">{t.status.open}</div>
        </div>
        <div 
          onClick={() => setStatusFilter(statusFilter === 'mitigated' ? 'all' : 'mitigated')}
          className={`bg-white dark:bg-gray-800 rounded-lg p-4 border cursor-pointer transition-colors ${
            statusFilter === 'mitigated' ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200 dark:border-gray-700 hover:border-green-300'
          }`}
        >
          <div className="text-2xl font-bold text-green-600">{stats.mitigated}</div>
          <div className="text-xs text-gray-500">{t.status.mitigated}</div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={`${t.actions.search} ${t.nav.risks.toLowerCase()}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          />
        </div>
        
        {(levelFilter !== 'all' || statusFilter !== 'all') && (
          <button
            onClick={() => { setLevelFilter('all'); setStatusFilter('all'); }}
            className="text-sm text-indigo-600 hover:text-indigo-800"
          >
            {t.actions.clear}
          </button>
        )}
      </div>

      {/* Risk List */}
      <div className="space-y-3">
        {filteredRisks.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <AlertOctagon className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{risks.length === 0 ? t.common.noResults : t.common.noResults}</p>
          </div>
        ) : (
          filteredRisks.map(risk => (
            <div
              key={risk.id}
              ref={risk.id === selectedRiskId ? selectedRiskRef : undefined}
              onClick={() => canEdit && setEditingRisk(risk)}
              className={`bg-white dark:bg-gray-800 rounded-xl border-l-4 border transition-all hover:shadow-lg ${canEdit ? 'cursor-pointer hover:scale-[1.01]' : ''} ${getLevelColor(getRiskLevel(risk))} border-r border-t border-b ${risk.id === selectedRiskId ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-gray-900' : 'border-gray-200 dark:border-gray-700'}`}
            >
              <div className="p-4">
                {/* Header Row */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      {getStatusIcon(risk.status)}
                      <h3 className="font-semibold text-gray-900 dark:text-white">{risk.risk_name}</h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{risk.description}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getLevelBadgeClass(getRiskLevel(risk))}`}>
                      {getRiskLevel(risk)}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-300 capitalize">
                      {risk.status}
                    </span>
                  </div>
                </div>

                {/* Details Grid - Always Visible */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg mb-3">
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t.risks.likelihood}</div>
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className="w-4 h-4 text-blue-500" />
                      <span className="font-semibold text-gray-900 dark:text-white">{risk.likelihood}/5</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t.risks.impact}</div>
                    <div className="flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="font-semibold text-gray-900 dark:text-white">{risk.impact}/5</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t.controls.owner}</div>
                    <span className="font-medium text-gray-900 dark:text-white">{risk.owner || '-'}</span>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t.controls.nextReview}</div>
                    <span className="font-medium text-gray-900 dark:text-white">{risk.next_review_date || '-'}</span>
                  </div>
                </div>

                {/* Treatment - Always Visible if exists */}
                {risk.treatment && (
                  <div className="mb-3">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t.risks.treatment}</div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 border border-blue-100 dark:border-blue-800">
                      {risk.treatment}
                    </p>
                  </div>
                )}

                {/* Action Footer */}
                {canEdit && (
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-600">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Edit className="w-3.5 h-3.5" />
                      {t.common.clickToEdit}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(risk.id); }}
                      className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {t.actions.delete}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary Footer */}
      <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
        {t.common.showing} {filteredRisks.length} {t.common.of} {risks.length} {t.nav.risks.toLowerCase()}
      </div>

      {/* Add/Edit Risk Modal */}
      {(showAddModal || editingRisk) && (
        <RiskModal
          risk={editingRisk}
          onClose={() => {
            setShowAddModal(false);
            setEditingRisk(null);
          }}
          onSave={async (riskData) => {
            try {
              if (editingRisk) {
                await grcService.updateRisk({ ...riskData, id: editingRisk.id });
              } else {
                await grcService.createRisk(riskData as any);
              }
              await loadRisks();
              setShowAddModal(false);
              setEditingRisk(null);
            } catch (e) {
              console.error('Failed to save risk:', e);
            }
          }}
        />
      )}
    </div>
  );
};

// Risk Modal Component
interface RiskModalProps {
  risk: DBRisk | null;
  onClose: () => void;
  onSave: (data: Partial<DBRisk>) => Promise<void>;
}

const RiskModal: React.FC<RiskModalProps> = ({ risk, onClose, onSave }) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    risk_name: risk?.risk_name || '',
    description: risk?.description || '',
    likelihood: risk?.likelihood || 3,
    impact: risk?.impact || 3,
    owner: risk?.owner || '',
    status: risk?.status || 'open',
    treatment: risk?.treatment || '',
    next_review_date: risk?.next_review_date || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.risk_name.trim()) return;
    
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  const riskScore = formData.likelihood * formData.impact;
  const riskLevel = riskScore >= 20 ? 'Critical' : riskScore >= 12 ? 'High' : riskScore >= 6 ? 'Medium' : 'Low';

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {risk ? `${t.actions.edit} ${t.nav.risks}` : `${t.actions.add} ${t.nav.risks}`}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t.assets.name} *
            </label>
            <input
              type="text"
              value={formData.risk_name}
              onChange={(e) => setFormData({ ...formData, risk_name: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t.assets.name}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t.actions.description}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t.actions.description}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.risks.likelihood} (1-5)
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={formData.likelihood}
                onChange={(e) => setFormData({ ...formData, likelihood: parseInt(e.target.value) })}
                className="w-full"
              />
              <div className="text-center text-sm font-medium text-gray-600 dark:text-gray-400">
                {formData.likelihood}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.risks.impact} (1-5)
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={formData.impact}
                onChange={(e) => setFormData({ ...formData, impact: parseInt(e.target.value) })}
                className="w-full"
              />
              <div className="text-center text-sm font-medium text-gray-600 dark:text-gray-400">
                {formData.impact}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">Risk Score: </span>
            <span className={`font-bold ${
              riskLevel === 'Critical' ? 'text-red-600' :
              riskLevel === 'High' ? 'text-orange-600' :
              riskLevel === 'Medium' ? 'text-amber-600' : 'text-green-600'
            }`}>
              {riskScore} ({riskLevel})
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.controls.owner}
              </label>
              <input
                type="text"
                value={formData.owner}
                onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder={t.controls.owner}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="open">{t.status.open}</option>
                <option value="mitigated">{t.status.mitigated}</option>
                <option value="accepted">{t.status.accepted}</option>
                <option value="closed">{t.status.closed}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t.risks.treatment}
            </label>
            <textarea
              value={formData.treatment}
              onChange={(e) => setFormData({ ...formData, treatment: e.target.value })}
              rows={2}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t.risks.treatment}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t.controls.nextReview}
            </label>
            <input
              type="date"
              value={formData.next_review_date}
              onChange={(e) => setFormData({ ...formData, next_review_date: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {t.actions.cancel}
            </button>
            <button
              type="submit"
              disabled={saving || !formData.risk_name.trim()}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t.common.loading}
                </>
              ) : (
                risk ? t.actions.update : t.actions.add
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
